import React, { useEffect, useMemo, useState } from "react";
import { EuiFieldText, EuiFormControlLayout, EuiSelect } from "@elastic/eui";
import { useParams } from "react-router";
import {
  AnketaResourcesEnum,
  KeycloakOrganizations,
  useAnketaAuth,
  useApi,
} from "../../../";
import { useAnketaI18n } from "src/internationalization";

// Our own domains - never offered as customer domains.
const INTERNAL_DOMAINS = new Set(["medocino.net", "autonubil.de"]);

// Module-level caches keyed by `${catalogId}/${questionnaireId}` so the
// organization domains are fetched ONCE per questionnaire and reused across
// re-renders, modal re-opens, component re-mounts and Keycloak token refreshes
// (the token must not be a refetch trigger). `inflight` dedupes concurrent
// requests (e.g. several contact fields mounting at once).
const domainCache = new Map<string, string[]>();
const inflight = new Map<string, Promise<string[]>>();

/**
 * Customer e-mail input: the local part is free text, but the domain is
 * restricted to the mail domains of the organizations assigned to this
 * questionnaire (excluding internal medocino/autonubil domains). This mirrors
 * the Keycloak organization model (orgs are keyed by mail domain), so a contact
 * can only belong to a customer organization that actually exists.
 *
 * Rendered for OpenQuestions whose TextValidator subType is CustomerEmail.
 */
export const CustomerEmailInput = ({
  value,
  onChange,
  onBlur,
  invalid,
  readOnly,
  placeholder,
}: {
  value: string;
  onChange: (val: string) => void;
  onBlur?: (val: string) => void;
  invalid?: boolean;
  readOnly?: boolean;
  placeholder?: string;
}): JSX.Element => {
  const { catalogId, questionnaireId } = useParams();
  const { keycloak } = useAnketaAuth();
  const { Get } = useApi();
  const { translate } = useAnketaI18n();

  const [domains, setDomains] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const parsed = useMemo(() => {
    const v = (value ?? "").trim();
    const at = v.lastIndexOf("@");
    return at >= 0
      ? { local: v.slice(0, at), domain: v.slice(at + 1) }
      : { local: v, domain: "" };
  }, [value]);

  const [local, setLocal] = useState<string>(parsed.local);
  const [domain, setDomain] = useState<string>(parsed.domain);

  // Keep in sync with external value changes.
  useEffect(() => {
    setLocal(parsed.local);
    setDomain(parsed.domain);
  }, [parsed.local, parsed.domain]);

  // Resolve the customer organization domains for this questionnaire. Cached at
  // module level and fetched at most once per questionnaire - token refreshes,
  // re-renders and re-mounts reuse the cached result instead of refetching.
  useEffect(() => {
    if (!catalogId || !questionnaireId) return;
    const key = `${catalogId}/${questionnaireId}`;
    let cancelled = false;

    const apply = (found: string[]) => {
      if (cancelled) return;
      setDomains(found);
      setLoading(false);
      // Default the domain only if none selected yet (don't clobber a parsed one).
      setDomain((current) => current || (found.length > 0 ? found[0] : ""));
    };

    const cached = domainCache.get(key);
    if (cached) {
      apply(cached);
      return;
    }

    const token = keycloak?.token;
    if (!token) return; // wait until a token is available, then refetch once

    setLoading(true);
    let promise = inflight.get(key);
    if (!promise) {
      promise = Get<Array<KeycloakOrganizations>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/${catalogId}/${questionnaireId}/organizations`,
        responseType: "json",
        token,
      })
        .then((response) => {
          const orgs = Array.isArray(response.data)
            ? response.data.filter(Boolean)
            : [];
          const found = Array.from(
            new Set(
              orgs
                .flatMap((org) => (org?.domains ?? []).map((d) => d?.name))
                .filter((d): d is string => Boolean(d))
                .map((d) => d.toLowerCase())
                .filter((d) => !INTERNAL_DOMAINS.has(d)),
            ),
          );
          domainCache.set(key, found);
          return found;
        })
        .finally(() => inflight.delete(key));
      inflight.set(key, promise);
    }

    promise.then(apply).catch(() => {
      if (!cancelled) {
        setDomains([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogId, questionnaireId, keycloak?.token]);

  const combine = (l: string, d: string): string => {
    const localPart = l.trim();
    return localPart === "" || d === "" ? localPart : `${localPart}@${d}`;
  };

  const domainOptions = domains.map((d) => ({ value: d, text: `@${d}` }));

  const domainControl =
    domains.length > 0 ? (
      <EuiSelect
        compressed
        aria-label={translate("anketa.questionnaire.editor.email.domain.aria", {
          defaultMessage: "Customer domain",
        })}
        options={domainOptions}
        value={domain}
        disabled={readOnly}
        isLoading={loading}
        onChange={(e) => {
          setDomain(e.target.value);
          onChange(combine(local, e.target.value));
          onBlur?.(combine(local, e.target.value));
        }}
      />
    ) : (
      // No customer organization/domain available yet.
      <EuiSelect
        compressed
        aria-label={translate("anketa.questionnaire.editor.email.domain.aria", {
          defaultMessage: "Customer domain",
        })}
        options={[
          {
            value: "",
            text: loading
              ? translate("anketa.questionnaire.editor.email.domain.loading", {
                  defaultMessage: "loading…",
                })
              : translate("anketa.questionnaire.editor.email.domain.none", {
                  defaultMessage: "no customer domain",
                }),
          },
        ]}
        value=""
        disabled
        isLoading={loading}
      />
    );

  return (
    <EuiFormControlLayout readOnly={readOnly} fullWidth append={domainControl}>
      <EuiFieldText
        controlOnly
        placeholder={
          placeholder ||
          translate("anketa.questionnaire.editor.email.local.placeholder", {
            defaultMessage: "firstname.lastname",
          })
        }
        isInvalid={invalid}
        readOnly={readOnly}
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          onChange(combine(e.target.value, domain));
        }}
        onBlur={(e) => onBlur?.(combine(e.target.value, domain))}
      />
    </EuiFormControlLayout>
  );
};
